import { useQuery, UseQueryOptions } from 'react-query';
import {
  Contact,
  GetContactsResponse,
  GetUserDetailsResponse,
} from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { CustomAxios, getResponseData } from '../../redux/axios';
import { isDevEnv } from '../../config/app';

// GetUserDetails (used here for mfa status) requires a Location-Id header -
// auth-api rejects the request outright with "Unable to retrieve location
// id" without one - even though mfaEnabled isn't location scoped. It also
// requires the USER_WRITE permission (checked globally for Weave-type
// tokens, independent of the location id supplied) unless asking for self.
// The User Lookup page isn't nested under a location route, so we send a
// placeholder location purely to satisfy the header requirement, mirroring
// the same hardcoded-location hack used in user-search.api.ts's
// searchUserById.
const getFallbackLocationId = () =>
  isDevEnv()
    ? // mobiledev
      'cb936c1e-529e-4f7d-84e1-ee4b9f67e8c8'
    : // witest
      'a8da7d2a-f875-40fe-adce-9cb9c41acd45';

const getUserDetails = (userId: string) =>
  CustomAxios.get<GetUserDetailsResponse>(
    `/auth-api/v3/users/details?userId=${encodeURIComponent(userId)}`,
    { headers: { 'Location-Id': getFallbackLocationId() } }
  ).then(getResponseData);

export function useGetUserDetails(
  userId: string,
  options?: UseQueryOptions<GetUserDetailsResponse, Error>
) {
  return useQuery<GetUserDetailsResponse, Error>(
    ['userDetails', userId],
    () => getUserDetails(userId),
    {
      enabled: !!userId,
      ...options,
    }
  );
}

// GetContacts has no location or ACL requirement (only userId validation),
// so use it directly for the contacts list rather than relying on
// GetUserDetails, which can fail/omit data outside of a location context.
const getContacts = (userId: string) =>
  CustomAxios.get<GetContactsResponse>(
    `/auth-api/v3/contacts?userId=${encodeURIComponent(userId)}`
  ).then(getResponseData);

export function useGetContacts(
  userId: string,
  options?: UseQueryOptions<Contact[], Error>
) {
  return useQuery<Contact[], Error>(
    ['userContacts', userId],
    () => getContacts(userId).then((res) => res.contacts ?? []),
    {
      enabled: !!userId,
      ...options,
    }
  );
}
